/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@/components/ui/input-otp';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { KeyRound, Mail, RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react';
import { requestPrescriptionOtp, signPrescription } from '@/lib/prescriptionExtras';

/**
 * Modal that walks the doctor through the OTP e-signature of a prescription:
 *   1. On open, POST /otp → sends 6-digit code by email + starts countdown
 *   2. Doctor types the 6 digits into shadcn/ui InputOTP
 *   3. POST /sign → status becomes 'signed', ready for SISS export
 *
 * Props
 *   docId        prescription id to sign (required when open)
 *   open, onOpenChange
 *   onSigned     (signResult) => void
 */
export const SignPrescriptionDialog = ({ docId, open, onOpenChange, onSigned }) => {
  const [sending, setSending] = useState(false);
  const [signing, setSigning] = useState(false);
  const [otp, setOtp] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [remaining, setRemaining] = useState(0);
  const [alreadySigned, setAlreadySigned] = useState(false);

  const requestOtp = async () => {
    if (!docId) return;
    setSending(true);
    setOtp('');
    try {
      const res = await requestPrescriptionOtp(docId);
      if (res.already_signed) {
        setAlreadySigned(true);
        toast.info('Ricetta già firmata');
        return;
      }
      setSentTo(res.sent_to || '');
      setRemaining(res.expires_in_seconds || 300);
      toast.success('OTP inviato via email');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio OTP');
    } finally { setSending(false); }
  };

  // On open, auto-request the OTP (unless already open with a live countdown)
  useEffect(() => {
    if (open && docId && !sentTo) requestOtp();
    if (!open) { setOtp(''); setSentTo(''); setRemaining(0); setAlreadySigned(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docId]);

  // Countdown
  useEffect(() => {
    if (!remaining) return;
    const t = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [remaining]);

  const doSign = async () => {
    if (!docId) return;
    if (otp.length < 6) { toast.error('Inserisci le 6 cifre'); return; }
    setSigning(true);
    try {
      const res = await signPrescription(docId, otp);
      toast.success('Ricetta firmata elettronicamente');
      onSigned?.(res);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Firma fallita');
    } finally { setSigning(false); }
  };

  const mmss = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="sign-prescription-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" /> Firma OTP
          </DialogTitle>
          <DialogDescription>
            La ricetta viene firmata elettronicamente con un codice OTP inviato alla tua email registrata.
            Solo dopo la firma la ricetta può essere trasmessa a SISS.
          </DialogDescription>
        </DialogHeader>

        {alreadySigned ? (
          <div className="flex items-center gap-2 p-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-sm">
            <ShieldCheck className="h-4 w-4 text-emerald-700" />
            Ricetta già firmata. Puoi trasmetterla a SISS.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-4 w-4" />
              Codice inviato a <span className="font-medium text-foreground">{sentTo || '…'}</span>
              {remaining > 0 && <Badge variant="outline" className="ml-auto tabular-nums">valido {mmss}</Badge>}
              {remaining === 0 && sentTo && (
                <Badge variant="destructive" className="ml-auto">scaduto</Badge>
              )}
            </div>

            <div className="flex justify-center py-2">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <AlertTriangle className="h-3 w-3 mt-0.5" />
              <span>
                In produzione: SMTP Gmail invia il codice al medico. In sviluppo: cerca il codice nei log del backend.
              </span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {!alreadySigned && (
            <Button variant="outline" onClick={requestOtp} disabled={sending} data-testid="otp-resend">
              {sending ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Rigenera OTP
            </Button>
          )}
          <Button onClick={doSign} disabled={alreadySigned || signing || otp.length < 6 || remaining === 0} data-testid="otp-confirm">
            <KeyRound className="h-4 w-4 mr-1" /> Firma ricetta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignPrescriptionDialog;
